import { useCallback, useMemo, useState } from 'react';
import { nanoid } from 'nanoid';
import { JSONContent } from '@tiptap/core';

import { SettingsHr } from 'approot/activity-builder/settings/settings.style';
import { Box } from 'approot/shared/design-system/box/box.style';
import {
  DRAFT_ACTION_COMPONENT_AUDIO_ADD,
  DRAFT_ACTION_COMPONENT_AUDIO_UPDATE,
  DRAFT_ACTION_COMPONENT_CLEAR_IMAGE,
} from 'global/constants';
import {
  SettingProps,
  InteractiveResponseCategoriseItem,
} from '../interactive-response-categorise.constants';
import { useInteractiveResponseCategoriseUpdate } from './interactive-response-categorise.hooks';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import {
  SortableContent,
  SortableHeader,
  SortableItem,
} from 'approot/shared/sortable/sortable.style';
import ComponentImageField from 'approot/activity-builder/components/shared/component-image-field/component-image-field';
import ComponentImageModal from 'approot/activity-builder/components/shared/component-image-modal/component-image-modal';
import AutoResizingTextArea from 'approot/shared/auto-resizing-textarea/auto-resizing-textarea';
import ComponentAudioModal from 'approot/activity-builder/components/shared/component-audio-modal/component-audio-modal';
import InteractiveResponseAttachAudio from '../../shared/interactive-response-attach-audio';
import InteractiveResponseDeleteItem from '../../shared/interactive-response-delete-item';
import { INTERACTIVE_RESPONSE_TEXT_IMAGE_MAX_LENGTH } from '../../shared/interactive-response-shared.constants';

const InteractiveResponseCategoriseItemsTextImage = (
  props: SettingProps & {
    item: InteractiveResponseCategoriseItem;
    includeText: boolean;
    'data-selector'?: string;
  }
) => {
  const {
    properties,
    item,
    includeText,
    'data-selector': dataSelector,
  } = props;

  const [editingId, setEditingId] = useState<string | null>(null);

  const { isVisible, openModal, closeModal } = useSimpleModalState();
  const {
    isVisible: isAudioModalVisible,
    openModal: openAudioModal,
    closeModal: closeAudioModal,
  } = useSimpleModalState();

  const { loading, handleUpdate } =
    useInteractiveResponseCategoriseUpdate(props);

  const selectedItem = useMemo(
    () => (editingId === item.itemId ? item : null),
    [editingId, item]
  );

  const handleUpdateItemText = useCallback(
    ({
      itemId,
      text,
      richtext,
      baseColour,
    }: {
      itemId: string;
      text: string | null;
      richtext: JSONContent | null;
      baseColour: string | null;
    }) => {
      const items = properties.interactiveResponseCategorise.items.map(i => {
        if (i.itemId === itemId) {
          return {
            ...i,
            text,
            richtext,
            baseColour,
          };
        }
        return i;
      });

      handleUpdate({
        ...properties,
        interactiveResponseCategorise: {
          ...properties.interactiveResponseCategorise,
          items,
        },
      });
    },
    [properties, handleUpdate]
  );

  return (
    <>
      {isVisible && selectedItem && (
        <ComponentImageModal
          isImageRequired
          loading={loading}
          containerDimensions={{
            width: 200,
            height: 200,
          }}
          image={selectedItem.image || undefined}
          sourceUrl={selectedItem.sourceUrl}
          altText={selectedItem.image?.description}
          cropData={
            !selectedItem.imageMode
              ? {
                  naturalWidth: 0,
                  naturalHeight: 0,
                  x: 0.5,
                  y: 0.5,
                  width: 0,
                  height: 0,
                  zoom: 1,
                }
              : selectedItem.cropData
          }
          closeModal={closeModal}
          onUpdateData={data => {
            handleUpdate(
              {
                ...properties,
                interactiveResponseCategorise: {
                  ...properties.interactiveResponseCategorise,
                  items: properties.interactiveResponseCategorise.items.map(
                    item => {
                      if (item.itemId === selectedItem.itemId) {
                        return {
                          ...item,
                          sourceUrl: data.sourceUrl,
                          imageMode: data.imageLayout,
                          image: data.image
                            ? {
                                __typename: 'InteractiveResponseImage',
                                key: data.image.key,
                                fileName: data.image.fileName,
                                description: data.altText || '',
                                data: {
                                  flip: data.image.data?.flip || null,
                                  __typename: 'ImageData',
                                },
                                itemId: item.image?.itemId || nanoid(),
                                original: item.image?.original || null,
                              }
                            : null,
                          cropData: data.cropData
                            ? {
                                ...data.cropData,
                                __typename: 'CropData',
                              }
                            : null,
                        };
                      }
                      return item;
                    }
                  ),
                },
              },
              {
                isSaveImmediate: true,
                onCompleted: closeModal,
              }
            );
          }}
        />
      )}

      {isAudioModalVisible && (
        <ComponentAudioModal
          loading={loading}
          fileKey={selectedItem?.audio?.key || ''}
          fileName={selectedItem?.audio?.fileName || ''}
          original={selectedItem?.audio?.original || null}
          closeModal={closeAudioModal}
          onUpdate={data => {
            handleUpdate(
              {
                ...properties,
                interactiveResponseCategorise: {
                  ...properties.interactiveResponseCategorise,
                  items: properties.interactiveResponseCategorise.items.map(
                    item => {
                      if (item.itemId === editingId) {
                        return {
                          ...item,
                          audio: data
                            ? {
                                __typename: 'InteractiveResponseAudio',
                                itemId: item.itemId,
                                key: data.key || '',
                                fileName: data.fileName || '',
                                original: data.original || null,
                              }
                            : null,
                        };
                      }
                      return item;
                    }
                  ),
                },
              },
              {
                draftAction: selectedItem?.audio?.key
                  ? DRAFT_ACTION_COMPONENT_AUDIO_UPDATE
                  : DRAFT_ACTION_COMPONENT_AUDIO_ADD,
                isSaveImmediate: true,
                onCompleted: closeAudioModal,
              }
            );
          }}
        />
      )}

      <>
        <SortableItem as="li" data-selector={dataSelector}>
          <SortableHeader>
            <div />
            <InteractiveResponseDeleteItem
              loading={loading}
              onDelete={onCompleted =>
                handleUpdate(
                  {
                    ...properties,
                    interactiveResponseCategorise: {
                      ...properties.interactiveResponseCategorise,
                      items:
                        properties.interactiveResponseCategorise.items.filter(
                          q => q.itemId !== item.itemId
                        ),
                    },
                  },
                  {
                    isSaveImmediate: true,
                    draftAction: DRAFT_ACTION_COMPONENT_CLEAR_IMAGE,
                    onCompleted,
                  }
                )
              }
            />
          </SortableHeader>
          <SortableContent>
            <ComponentImageField
              contentsOnly
              image={
                item.image
                  ? {
                      ...item.image,
                      alt: item.image?.description || null,
                    }
                  : null
              }
              ariaLabel="Edit image"
              onEdit={() => {
                setEditingId(item.itemId);
                openModal();
              }}
            />
            {includeText && (
              <Box $marginTop={12}>
                <AutoResizingTextArea
                  showCounter
                  maxLength={INTERACTIVE_RESPONSE_TEXT_IMAGE_MAX_LENGTH}
                  id={`${item.itemId}-text`}
                  initialValue={item.text || ''}
                  onChange={e => {
                    handleUpdateItemText({
                      itemId: item.itemId,
                      text: e.target.value,
                      richtext: item.richtext,
                      baseColour: item.baseColour,
                    });
                  }}
                />
              </Box>
            )}
          </SortableContent>
          <SettingsHr $marginBottom={0} />
          <SortableContent>
            <InteractiveResponseAttachAudio
              audioFileName={item.audio?.fileName || null}
              onClick={() => {
                setEditingId(item.itemId);
                openAudioModal();
              }}
            />
          </SortableContent>
        </SortableItem>
      </>
    </>
  );
};

export default InteractiveResponseCategoriseItemsTextImage;
